/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 * The component for News routing
 *
 * @author Evgeniy Naumov
 * @version 1.25.1
 * @since 1.25.1
 */
import { useSelector } from 'react-redux';
import { Switch } from 'react-router';

import { EBreadCrumbType } from '@/main/modules/breadCrumbs/breadCrumbsTypes';
import { AccessComponentKeyEnum } from '@/main/modules/role/constants/guard/guardConstants';
import CorporatePage from '@/main/pages/CorporatePage/CorporatePage';
import PageNotFound from '@/main/pages/ErrorPage/PageNotFound';
import {
    makeSelectAuthoritiesIsNewsServiceUser,
    makeSelectNewsServiceAccessLoggedIn,
} from '@/main/pages/PageAuth/selectors/authSelectors';
import PageNews from '@/main/pages/PageNews/Loadable';
import PageNewsCreate from '@/main/pages/PageNews/LoadableCreate';
import PageNewsManage from '@/main/pages/PageNews/LoadableManage';
import PageNewsPreview from '@/main/pages/PageNews/PageNewsPreview';

import { RoutePath } from '../constants/routesConstants';
import PrivateRoute from '../PrivateRoute';

export const NewsRoute = () => {
    const isNewsServiceUserLoggedIn = useSelector(makeSelectNewsServiceAccessLoggedIn());
    const isNewsServiceAccessAutorities = useSelector(makeSelectAuthoritiesIsNewsServiceUser());

    // Service news user routes
    if (isNewsServiceUserLoggedIn && isNewsServiceAccessAutorities)
        return (
            <Switch>
                <PrivateRoute
                    exact
                    accessKey={AccessComponentKeyEnum.PageNews}
                    component={PageNewsManage}
                    path={RoutePath.news}
                    wrapper={CorporatePage}
                />
                <PrivateRoute
                    exact
                    accessKey={AccessComponentKeyEnum.PageNews}
                    component={PageNewsCreate}
                    path={RoutePath.newsCreate}
                    wrapper={CorporatePage}
                />
                <PrivateRoute
                    accessKey={AccessComponentKeyEnum.PageNews}
                    component={PageNewsPreview}
                    path={RoutePath.newsPreview}
                    wrapper={CorporatePage}
                />
                <PrivateRoute accessKey={AccessComponentKeyEnum.PageNotFound} component={PageNotFound} path="**" />
            </Switch>
        );

    // Default news routes
    return (
        <Switch>
            <PrivateRoute
                exact
                accessKey={AccessComponentKeyEnum.PageNews}
                breadCrumbs={[EBreadCrumbType.news]}
                component={PageNews}
                path={RoutePath.news}
                wrapper={CorporatePage}
            />
            <PrivateRoute
                accessKey={AccessComponentKeyEnum.PageNews}
                breadCrumbs={[EBreadCrumbType.news]}
                component={PageNews}
                path={RoutePath.newsId}
                wrapper={CorporatePage}
            />
            <PrivateRoute accessKey={AccessComponentKeyEnum.PageNotFound} component={PageNotFound} path="**" />
        </Switch>
    );
};

export default NewsRoute;
